import { Injectable } from '@angular/core';
import { switchMap } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of } from 'rxjs';
import * as CompaniesActions from './companies.actions';
import { CompaniesService } from 'app/modules/masters/companies/companies.service';

@Injectable()
export class CompaniesEffects {
  constructor(
    private actions$: Actions,
    private companiesService: CompaniesService,
  ) {}

  /** 🔄 Load all companies */
  loadCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.loadCompanies),
      mergeMap(() =>
        this.companiesService.getCompanies().pipe(
          map((res) => CompaniesActions.loadCompaniesSuccess({ companies: res.items })),
          catchError((error) =>
            of(CompaniesActions.loadCompaniesFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  /** 📄 Load paginated companies */
  loadCompaniesPaginated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.loadCompaniesPaginated),
      mergeMap(({ page, limit, sortBy, sortOrder, stockOnly = false, status }) =>
        this.companiesService.getAllPaginated(page, limit, sortBy, sortOrder, stockOnly, true, status).pipe(
          map((response) =>
            CompaniesActions.loadCompaniesPaginatedSuccess(response)
          ),
          catchError((error) =>
            of(CompaniesActions.loadCompaniesFailure({ error: error.message }))
          )
        )
      )
    )
  );

  /** ➕ Create new company */
  createCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.createCompany),
      mergeMap(({ company }) =>
        this.companiesService.createCompany(company).pipe(
          map((res) => {
            // Handle both response formats: res.data or direct res
            const createdCompany = res.data || res;
            console.log('✅ Company create response:', createdCompany);
            return CompaniesActions.createCompaniesuccess({ company: createdCompany });
          }),
          catchError((error) => {
            const message =
              error?.status === 409 && error?.error?.message
                ? error.error.message
                : error?.message ?? 'An error occurred';
            return of(CompaniesActions.createCompanyFailure({ error: message }));
          }),
        ),
      ),
    ),
  );

  /** 🔁 Refresh list after creation */
  createCompanySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.createCompaniesuccess),
      map(() => CompaniesActions.loadCompanies()),
    ),
  );

  /** ✏️ Update company */
  updateCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.updateCompany),
      mergeMap(({ id, changes }) =>
        this.companiesService.updateCompany(id, changes).pipe(
          map((res) => {
            // Backend returns company directly or wrapped in data property
            const company = res.data || res;
            console.log('✅ Company update response:', company);
            return CompaniesActions.updateCompanySuccess({ company });
          }),
          catchError((error) => {
            const message =
              (error?.status === 409 || error?.status === 400) && error?.error?.message
                ? error.error.message
                : error?.message ?? 'An error occurred';
            return of(CompaniesActions.updateCompanyFailure({ error: message }));
          }),
        ),
      ),
    ),
  );

  // Note: List refresh is handled by the component to maintain pagination state
  // No need to trigger loadCompanies() here as it would load all companies

  /** 🗑️ Delete company */
  deleteCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.deleteCompany),
      mergeMap(({ id }) =>
        this.companiesService.deleteCompany(id).pipe(
          map(() => CompaniesActions.deleteCompanySuccess({ id })),
          catchError((error) =>
            of(CompaniesActions.deleteCompanyFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  /** 🔁 Refresh list after delete */
  deleteCompanySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.deleteCompanySuccess),
      map(() => CompaniesActions.loadCompanies()),
    ),
  );

  /** 🔍 Search companies with pagination */
  searchCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.searchCompanies),
      mergeMap(({ query, skip = 0, take = 10, rowIndex, sortBy = 'name', sortOrder = 'asc', stockOnly = false, status }) =>
        this.companiesService.searchCompanies(query, skip, take, undefined, sortBy, sortOrder, stockOnly, true, status).pipe(
          // 🔧 FIXED LINE
          map((res) =>
            CompaniesActions.searchCompaniesSuccess({
              companies: res.items,
              total: res.total,
              rowIndex,
            }),
          ),
          catchError((error) =>
            of(CompaniesActions.searchCompaniesFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  /** 🔍 Autocomplete: Search Manufacturers by name */
  searchManufacturers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.searchManufacturers),
      mergeMap(({ query }) =>
        this.companiesService.searchCompanies(query, 0, 10, 'MFG').pipe(
          map((res) =>
            CompaniesActions.searchManufacturersSuccess({
              manufacturers: res.items,
            }),
          ),
          catchError((error) =>
            of(
              CompaniesActions.searchManufacturersFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  /** ✅ Check if short name is available */
  checkShortNameAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.checkShortNameAvailability),
      mergeMap(({ shortName, excludeId }) =>
        this.companiesService.checkShortNameAvailability(shortName, excludeId).pipe(
          map((available) => CompaniesActions.checkShortNameAvailabilitySuccess({ available })),
          catchError((error) =>
            of(
              CompaniesActions.checkShortNameAvailabilityFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );
  //company availability
  checkCompanyNameAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.checkCompanyNameAvailability),
      switchMap(({ name }) =>
        this.companiesService.checkCompanyNameExists(name).pipe(
          map((exists: boolean) =>
            CompaniesActions.checkCompanyNameAvailabilitySuccess({ exists }),
          ),
          catchError((error) =>
            of(
              CompaniesActions.checkCompanyNameAvailabilityFailure({
                error: error.message || 'Failed to check name availability',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  /** ✅ Check if alter code is available */
  checkAlterCodeAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompaniesActions.checkAlterCodeAvailability),
      mergeMap(({ alterCode, excludeId }) =>
        this.companiesService.checkAlterCodeAvailability(alterCode, excludeId).pipe(
          map((available) => CompaniesActions.checkAlterCodeAvailabilitySuccess({ available })),
          catchError((error) =>
            of(
              CompaniesActions.checkAlterCodeAvailabilityFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
